import React, { FC, useState, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styles from '../../opm-summary-waterfall-erf.module.scss';
import { useLocale } from '@app/core/hooks/useLocale';
import {
  OpmFirstLastWaterfallDictionaryDto,
  FirstLastInstrument,
  FirstLastInstrumentTransactionSummary,
  FirstLastSummaryIndexDictionaryDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import classNames from 'classnames';
import InstrumentInterimCashFlows from './interim-cashflow-movements/InstrumentInterimCashFlows';
import HorizontalTableDivider from '@app/shared/components/table/HorizontalTableDivider';
import SvgChevron from '@app/shared/icons/Chevron';
import { setFirstLastWaterfallTableExpandState } from '@app/core/store/ui-values-slice';
import InstrumentExitDistributions from './InstrumentExitDistributions';

export type InstrumentValueKeys = Pick<
  FirstLastInstrument,
  'proceedsCap' | 'discountFactor' | 'discountedFairValue'
>;
export type InstrumentSummaryKeys = Pick<
  FirstLastInstrumentTransactionSummary,
  | 'totalInfusion'
  | 'totalRedemption'
  | 'totalCashPay'
  | 'totalDividends'
  | 'totalCashFlowMovementFairValue'
  | 'cumulativeCashMovementFairValue'
>;
interface InstrumentCalculationsErfProps {
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto;
  selectedYear: string;
}

export interface FirstLastExpandedSectionsState {
  valuation: boolean;
  instruments: {
    [instrumentKey: string]: {
      expanded: boolean;
      interimCashFlows: boolean;
      exitDistributions: boolean;
    };
  };
}

const InstrumentCalculationsErf: FC<InstrumentCalculationsErfProps> = ({
  opmWaterfall,
  selectedYear,
}): JSX.Element => {
  const { l } = useLocale();
  const interpolationOption = { escapeValue: false };
  const waterfallIterations = useAppSelector((state) => state.uiValues.opmWaterfallIterations ?? 5);
  const titleColSpan = waterfallIterations * 2 + 1;
  const firstSimulation = Object.keys(opmWaterfall[selectedYear].first)[0];
  const dispatch = useDispatch();

  const allInstrumentKeys = Object.keys(
    opmWaterfall[selectedYear].first[firstSimulation].instruments
  ).filter(
    (key) => opmWaterfall[selectedYear].first[firstSimulation].instruments[key].shouldBeValued
  );

  const storedExpandableSectionsState = useAppSelector(
    (state) => state.uiValues.userSelections.firstLastWaterfallTableExpandStates
  );

  // Initialize expanded state for all instruments
  const defaultExpandedSections: FirstLastExpandedSectionsState = useMemo(() => {
    if (storedExpandableSectionsState && Object.keys(storedExpandableSectionsState).length > 0) {
      return storedExpandableSectionsState;
    }
    const instrumentsState: {
      [instrumentKey: string]: {
        expanded: boolean;
        interimCashFlows: boolean;
        exitDistributions: boolean;
      };
    } = {};
    allInstrumentKeys.forEach((instrumentKey) => {
      instrumentsState[instrumentKey] = {
        expanded: true,
        interimCashFlows: true,
        exitDistributions: true,
      };
    });
    return {
      valuation: true,
      instruments: instrumentsState,
    };
  }, [allInstrumentKeys, storedExpandableSectionsState]);

  const [expanded, setExpanded] = useState<FirstLastExpandedSectionsState>(defaultExpandedSections);

  useEffect(() => {
    dispatch(setFirstLastWaterfallTableExpandState(defaultExpandedSections));
  }, [defaultExpandedSections, dispatch]);

  // Sync local expanded state with Redux when Redux state changes
  useEffect(() => {
    if (
      storedExpandableSectionsState &&
      JSON.stringify(storedExpandableSectionsState) !== JSON.stringify(expanded)
    ) {
      setExpanded(storedExpandableSectionsState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedExpandableSectionsState]);

  const handleValuationToggle = () => {
    setExpanded((prev) => {
      const updated = { ...prev, valuation: !prev.valuation };
      dispatch(setFirstLastWaterfallTableExpandState(updated));
      return updated;
    });
  };

  const handleInstrumentToggle = (
    instrumentKey: string,
    section: 'expanded' | 'interimCashFlows' | 'exitDistributions'
  ) => {
    if (
      section !== 'expanded' &&
      section !== 'interimCashFlows' &&
      section !== 'exitDistributions'
    ) {
      return;
    }
    setExpanded((prev) => {
      const updated = {
        ...prev,
        instruments: {
          ...prev.instruments,
          [instrumentKey]: {
            expanded: prev.instruments[instrumentKey]?.expanded ?? true,
            interimCashFlows: prev.instruments[instrumentKey]?.interimCashFlows ?? true,
            exitDistributions: prev.instruments[instrumentKey]?.exitDistributions ?? true,
            [section]: !prev.instruments[instrumentKey]?.[section],
          },
        },
      };
      dispatch(setFirstLastWaterfallTableExpandState(updated));
      return updated;
    });
  };

  return (
    <>
      <tr>
        <th
          colSpan={titleColSpan}
          className={styles['section-header']}
          data-testid="valuation-title">
          <div className={styles['expand-toggle--indented']}>
            <SvgChevron
              onClick={handleValuationToggle}
              className={
                expanded.valuation ? styles['chevron-expanded'] : styles['chevron-collapsed']
              }
            />
          </div>
          {l('_Valuation')}
        </th>
      </tr>
      {expanded.valuation && (
        <>
          {allInstrumentKeys.map((instrumentKey) => {
            const instrument =
              opmWaterfall[selectedYear].first[firstSimulation].instruments[instrumentKey];
            const instrumentHasTranches = instrument.numberOfPayoutLogicTranches > 0;

            const instrumentHasDloc = (() => {
              // Check if any simulation has a non-zero dlocPc value for this instrument
              const checkSimulations = (simulations: FirstLastSummaryIndexDictionaryDto) => {
                return Object.values(simulations).some((simulation) => {
                  const inst = simulation.instruments[instrumentKey];
                  if (!inst || !inst.tranches) {
                    return false;
                  }

                  return Object.values(inst.tranches).some((tranche) => {
                    return tranche.dlocPc != null && tranche.dlocPc !== 0;
                  });
                });
              };

              return (
                checkSimulations(
                  opmWaterfall[selectedYear].first as FirstLastSummaryIndexDictionaryDto
                ) ||
                checkSimulations(
                  opmWaterfall[selectedYear].last as FirstLastSummaryIndexDictionaryDto
                )
              );
            })();

            const isTotalAdditionPresent =
              typeof instrument.transactionSummary?.totalInfusion === 'number';
            const isTotalRedemptionPresent =
              typeof instrument.transactionSummary?.totalRedemption === 'number';
            const isCashInterestPresent =
              typeof instrument.transactionSummary?.totalCashPay === 'number';
            const isDividendPresent =
              typeof instrument.transactionSummary?.totalDividends === 'number';
            const shouldDisplayOtherCashflowMovement =
              isTotalAdditionPresent ||
              isTotalRedemptionPresent ||
              isCashInterestPresent ||
              isDividendPresent;
            const instrumentName = instrument.instrumentName;
            const instrumentExpanded = expanded.instruments[instrumentKey]?.expanded ?? true;

            return (
              <React.Fragment key={instrumentKey}>
                <tr>
                  <th
                    colSpan={titleColSpan}
                    className={classNames(styles['section-header'], styles['indentation-level-1'])}
                    data-testid={`instrument-title-${instrumentKey}`}>
                    <span className={styles['expand-toggle--indented']}>
                      <SvgChevron
                        onClick={() => handleInstrumentToggle(instrumentKey, 'expanded')}
                        className={
                          instrumentExpanded
                            ? styles['chevron-expanded']
                            : styles['chevron-collapsed']
                        }
                      />
                    </span>
                    {instrumentName}
                  </th>
                </tr>
                {instrumentExpanded && (
                  <>
                    {shouldDisplayOtherCashflowMovement && (
                      <InstrumentInterimCashFlows
                        expanded={{
                          interimCashFlows:
                            expanded.instruments[instrumentKey]?.interimCashFlows ?? true,
                        }}
                        handleToggle={(section) =>
                          handleInstrumentToggle(
                            instrumentKey,
                            section as 'expanded' | 'interimCashFlows' | 'exitDistributions'
                          )
                        }
                        titleColSpan={titleColSpan}
                        opmWaterfall={opmWaterfall}
                        selectedYear={selectedYear}
                        selectedInstrument={instrumentKey}
                        waterfallIterations={waterfallIterations}
                      />
                    )}
                    {instrumentHasTranches && <HorizontalTableDivider colSpan={titleColSpan} />}
                    <InstrumentExitDistributions
                      expanded={{
                        exitDistributions:
                          expanded.instruments[instrumentKey]?.exitDistributions ?? true,
                      }}
                      handleToggle={(section) =>
                        handleInstrumentToggle(
                          instrumentKey,
                          section as 'expanded' | 'interimCashFlows' | 'exitDistributions'
                        )
                      }
                      titleColSpan={titleColSpan}
                      opmWaterfall={opmWaterfall}
                      selectedYear={selectedYear}
                      waterfallIterations={waterfallIterations}
                      instrumentHasDloc={instrumentHasDloc}
                      instrumentHasTranches={instrumentHasTranches}
                      instrumentKey={instrumentKey}
                      instrument={instrument}
                      interpolationOption={interpolationOption}
                    />
                  </>
                )}
              </React.Fragment>
            );
          })}
        </>
      )}
    </>
  );
};

export default InstrumentCalculationsErf;
